'use client'

import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { intersectCaps, type ResolvedCaps } from '@/lib/configs/venue-capabilities'
import { parseExchangeField } from '@/lib/proto/util'
import { useUserAccounts } from '@/service/account.hook'
import { useVenueCapsLookup } from '@/service/venue-capabilities.hook'
import { orderFormSelectionAtomFamily } from '@/state/order'
import { Exchange, type TradeVenue } from '@/types/generated/common/types'

/**
 * The caps every row of the card supports. A row whose venue is unknown is skipped rather
 * than denying the card; with no venue left the card is unrestricted (the identity caps).
 */
export function cardCaps(
  rowVenues: (TradeVenue | undefined)[],
  lookup: (tv?: TradeVenue | null) => ResolvedCaps
): ResolvedCaps {
  const known = rowVenues.filter((tv): tv is TradeVenue => tv !== undefined)
  return intersectCaps(known.map((tv) => lookup(tv)))
}

/** The venue when every known row shares it, else undefined. */
export function sharedVenueOf(rowVenues: (TradeVenue | undefined)[]): TradeVenue | undefined {
  const known = rowVenues.filter((tv): tv is TradeVenue => tv !== undefined)
  return known.length > 0 && known.every((tv) => tv === known[0]) ? known[0] : undefined
}

export type OrderFormCaps = ResolvedCaps & {
  /** Set only when every known row shares one venue. */
  sharedVenue: TradeVenue | undefined
  rowVenues: TradeVenue[]
}

/**
 * What this order form can place: the intersection over its rows' venues. A row bound to an
 * account takes that account's venue; an inherited row takes its exchange's venue, which is the
 * venue its inherited account resolves in. An empty ticket uses the form's own venue
 * (`useOrderFormTradeVenue`), and with none the card is unrestricted.
 *
 * Memoised on the venue lookup, so put `caps` in memo/effect deps.
 */
export function useOrderFormCaps(cardId: string): OrderFormCaps {
  const { selectedInstruments, selectedOptions } = useAtomValue(
    orderFormSelectionAtomFamily(cardId)
  )
  const emptyTicketVenue = useOrderFormTradeVenue(cardId)
  const { data: userAccounts } = useUserAccounts()
  const lookup = useVenueCapsLookup()

  return useMemo(() => {
    const venueByAccount = new Map(
      (userAccounts?.accounts ?? []).map((account) => [account.accountId, account.tradeVenue])
    )
    const rowVenue = (
      item: { accountId?: string; exchange?: string },
      fallbackExchange?: string
    ): TradeVenue | undefined => {
      if (item.accountId && venueByAccount.has(item.accountId)) {
        return venueByAccount.get(item.accountId)
      }
      const exchange = item.exchange || fallbackExchange
      return exchange
        ? exchangeToTradeVenue(parseExchangeField(exchange, Exchange.EXCHANGE_UNSPECIFIED))
        : undefined
    }
    const rows = [
      ...selectedInstruments.map((inst) => rowVenue(inst)),
      ...selectedOptions.map((opt) =>
        rowVenue(opt, opt.ticker ? String(opt.ticker.exchange) : undefined)
      ),
    ]
    const venues = rows.length > 0 ? rows : [emptyTicketVenue]
    return {
      ...cardCaps(venues, lookup),
      sharedVenue: sharedVenueOf(venues),
      rowVenues: venues.filter((venue): venue is TradeVenue => venue !== undefined),
    }
  }, [selectedInstruments, selectedOptions, userAccounts, emptyTicketVenue, lookup])
}
